import { type FormEvent, useState } from 'react';
import { useCreateSupplier, useMe, useRef } from '../api/hooks';
import { AddressFields, type Address } from '../components/AddressFields';
import { ChatWidget } from '../components/ChatWidget';
import { RefSelect } from '../components/RefSelect';
import { clean, mergeStrings } from '../lib/form';

export function NewSupplierPage() {
  const create = useCreateSupplier();
  const me = useMe();
  const terms = useRef('terms');

  const [f, setF] = useState({
    displayName: '', companyName: '', title: '', givenName: '', familyName: '',
    email: '', phone: '', mobile: '', fax: '', website: '',
    acctNum: '', termId: '', taxId: '',
  });
  const [vendor1099, set1099] = useState(false);
  const [billAddr, setBill] = useState<Address>({});
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const applyExtract = (x: Record<string, unknown>) => {
    setF((cur) => mergeStrings(cur, x));
    if (x.billAddr && typeof x.billAddr === 'object') setBill((a) => ({ ...a, ...(x.billAddr as Address) }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(clean({ ...f, vendor1099: vendor1099 || undefined, billAddr }));
  };

  return (
    <>
      <h1>Add Supplier</h1>
      <ChatWidget
        formType="supplier"
        enabled={me.data?.assistantEnabled ?? false}
        onExtract={applyExtract}
        placeholder="e.g. “Iron Mountain, billing@ironmtn.com, acct 55-2231” — or attach a supplier invoice / statement."
      />
      <form onSubmit={submit} style={{ maxWidth: 720 }}>
        {create.isSuccess && <div className="banner ok">Supplier created in QuickBooks.</div>}
        {create.error && <div className="banner error">{(create.error as Error).message}</div>}

        <div className="card">
          <h2>Basics</h2>
          <div className="field">
            <label>Display name *</label>
            <input required value={f.displayName} onChange={(e) => set('displayName', e.target.value)} />
          </div>
          <div className="field">
            <label>Company name</label>
            <input value={f.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </div>
          <div className="row cols-3">
            <div className="field"><label>Title</label><input value={f.title} onChange={(e) => set('title', e.target.value)} /></div>
            <div className="field"><label>First name</label><input value={f.givenName} onChange={(e) => set('givenName', e.target.value)} /></div>
            <div className="field"><label>Last name</label><input value={f.familyName} onChange={(e) => set('familyName', e.target.value)} /></div>
          </div>
        </div>

        <div className="card">
          <h2>Contact</h2>
          <div className="row cols-2">
            <div className="field"><label>Email</label><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
            <div className="field"><label>Website</label><input value={f.website} onChange={(e) => set('website', e.target.value)} /></div>
          </div>
          <div className="row cols-3">
            <div className="field"><label>Phone</label><input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
            <div className="field"><label>Mobile</label><input value={f.mobile} onChange={(e) => set('mobile', e.target.value)} /></div>
            <div className="field"><label>Fax</label><input value={f.fax} onChange={(e) => set('fax', e.target.value)} /></div>
          </div>
          <AddressFields legend="Address" value={billAddr} onChange={setBill} />
        </div>

        <div className="card">
          <h2>Account &amp; tax</h2>
          <div className="row cols-2">
            <div className="field"><label>Account number (with supplier)</label><input value={f.acctNum} onChange={(e) => set('acctNum', e.target.value)} /></div>
            <RefSelect label="Terms" value={f.termId} onChange={(v) => set('termId', v)} options={terms.data} />
          </div>
          <div className="field"><label>Tax ID</label><input value={f.taxId} onChange={(e) => set('taxId', e.target.value)} /></div>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 500 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={vendor1099} onChange={(e) => set1099(e.target.checked)} /> Track for 1099
          </label>
        </div>

        <button className="primary" type="submit" disabled={create.isPending || !f.displayName.trim()}>
          {create.isPending ? 'Creating…' : 'Create supplier'}
        </button>
      </form>
    </>
  );
}
